import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import type {
  BackingCapabilities,
  CreateVclusterInput,
  Environment,
  EnvironmentBacking,
  EnvironmentMetrics,
  KubectlResult,
  LogLine,
  PodRef,
  ResourceGraph,
  ResourceKind,
  ResourceRow,
} from '@internal/plugin-environment-common';

export type BackingInfo = {
  backing: EnvironmentBacking;
  available: boolean;
  capabilities: BackingCapabilities;
};

export type LogOptions = {
  namespace?: string;
  deployment?: string;
  pod?: string;
  container?: string;
  tailLines?: number;
  timestamps?: boolean;
  previous?: boolean;
};

export interface EnvironmentApi {
  listBackings(): Promise<BackingInfo[]>;
  listEnvironments(): Promise<Environment[]>;
  getEnvironment(name: string, backing?: EnvironmentBacking): Promise<Environment>;

  /**
   * Starts a virtual cluster. Resolves as soon as it is requested: the
   * environment comes back pending and becomes healthy a minute or two later.
   */
  createVcluster(input: CreateVclusterInput): Promise<Environment>;

  /** Destroys an environment the console created. Warnings are things to do by hand. */
  deleteEnvironment(
    name: string,
    backing?: EnvironmentBacking,
  ): Promise<{ warnings: string[] }>;

  /** Returns the kubeconfig body and the filename the backend suggested. */
  getKubeconfig(
    name: string,
    backing?: EnvironmentBacking,
  ): Promise<{ filename: string; content: string }>;

  listNamespaces(name: string, backing?: EnvironmentBacking): Promise<string[]>;
  listResources(
    name: string,
    kind: ResourceKind,
    namespace?: string,
    backing?: EnvironmentBacking,
  ): Promise<ResourceRow[]>;
  getGraph(
    name: string,
    namespace?: string,
    backing?: EnvironmentBacking,
  ): Promise<ResourceGraph>;

  /** One resource rendered as YAML. Secret values are masked unless revealed. */
  getResourceYaml(
    name: string,
    kind: ResourceKind,
    resourceName: string,
    namespace?: string,
    reveal?: boolean,
    backing?: EnvironmentBacking,
  ): Promise<{ yaml: string; redacted: boolean }>;

  listPods(
    name: string,
    namespace?: string,
    deployment?: string,
    backing?: EnvironmentBacking,
  ): Promise<PodRef[]>;
  getLogs(
    name: string,
    options: LogOptions,
    backing?: EnvironmentBacking,
  ): Promise<LogLine[]>;

  getMetrics(
    name: string,
    rangeMinutes?: number,
    backing?: EnvironmentBacking,
  ): Promise<EnvironmentMetrics>;

  runKubectl(
    name: string,
    args: string[],
    backing?: EnvironmentBacking,
  ): Promise<KubectlResult>;
}

export const environmentApiRef = createApiRef<EnvironmentApi>({
  id: 'plugin.environment.service',
});

export class EnvironmentClient implements EnvironmentApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async base(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('environment');
  }

  /**
   * Performs a request and unwraps Backstage's error envelope.
   *
   * Without this the UI would show "500 Internal Server Error" instead of the
   * kubectl message that actually explains what went wrong.
   */
  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await this.fetchApi.fetch(`${await this.base()}${path}`, init);

    if (!res.ok) {
      let detail = `${res.status} ${res.statusText}`;
      try {
        const body = await res.json();
        if (body?.error?.message) detail = body.error.message;
      } catch {
        // Non-JSON body; the status line is all we have.
      }
      throw new Error(detail);
    }

    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  private q(params: Record<string, string | number | boolean | undefined>): string {
    const search = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') search.set(k, String(v));
    }
    const s = search.toString();
    return s ? `?${s}` : '';
  }

  private seg(v: string): string {
    return encodeURIComponent(v);
  }

  async listBackings(): Promise<BackingInfo[]> {
    const r = await this.request<{ items: BackingInfo[] }>('/backings');
    return r.items;
  }

  async listEnvironments(): Promise<Environment[]> {
    const r = await this.request<{ items: Environment[] }>('/environments');
    return r.items;
  }

  async getEnvironment(name: string, backing?: EnvironmentBacking) {
    return this.request<Environment>(
      `/environments/${this.seg(name)}${this.q({ backing })}`,
    );
  }

  async createVcluster(input: CreateVclusterInput) {
    return this.request<Environment>('/environments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ backing: 'vcluster', spec: input }),
    });
  }

  async deleteEnvironment(name: string, backing?: EnvironmentBacking) {
    return this.request<{ warnings: string[] }>(
      `/environments/${this.seg(name)}${this.q({ backing })}`,
      { method: 'DELETE' },
    );
  }

  async getKubeconfig(name: string, backing?: EnvironmentBacking) {
    const res =await this.fetchApi.fetch(
      `${await this.base()}/environments/${this.seg(name)}/kubeconfig${this.q({ backing })}`,
    );
    if (!res.ok) {
      let detail = `${res.status} ${res.statusText}`;
      try {
        const body = await res.json();
        if (body?.error?.message) detail = body.error.message;
      } catch {
        // fall through
      }
      throw new Error(detail);
    }

    // Prefer the filename the backend set, so the download matches the
    // environment rather than the route.
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const match = /filename="([^"]+)"/.exec(disposition);
    return {
      filename: match?.[1] ?? `${name}-kubeconfig.yaml`,
      content: await res.text(),
    };
  }

  async listNamespaces(name: string, backing?: EnvironmentBacking) {
    const r = await this.request<{ items: string[] }>(
      `/environments/${this.seg(name)}/namespaces${this.q({ backing })}`,
    );
    return r.items;
  }

  async listResources(
    name: string,
    kind: ResourceKind,
    namespace?: string,
    backing?: EnvironmentBacking,
  ) {
    const r = await this.request<{ items: ResourceRow[] }>(
      `/environments/${this.seg(name)}/resources/${kind}${this.q({ namespace, backing })}`,
    );
    return r.items;
  }

  async getResourceYaml(
    name: string,
    kind: ResourceKind,
    resourceName: string,
    namespace?: string,
    reveal?: boolean,
    backing?: EnvironmentBacking,
  ) {
    const path = `/environments/${this.seg(name)}/resources/${kind}/${this.seg(
      resourceName,
    )}/yaml`;
    return this.request<{ yaml: string; redacted: boolean }>(
      `${path}${this.q({ namespace, reveal, backing })}`,
    );
  }

  async getGraph(name: string, namespace?: string, backing?: EnvironmentBacking) {
    return this.request<ResourceGraph>(
      `/environments/${this.seg(name)}/graph${this.q({ namespace, backing })}`,
    );
  }

  async listPods(
    name: string,
    namespace?: string,
    deployment?: string,
    backing?: EnvironmentBacking,
  ) {
    const r = await this.request<{ items: PodRef[] }>(
      `/environments/${this.seg(name)}/pods${this.q({ namespace, deployment, backing })}`,
    );
    return r.items;
  }

  async getLogs(name: string, options: LogOptions, backing?: EnvironmentBacking) {
    const r = await this.request<{ items: LogLine[] }>(
      `/environments/${this.seg(name)}/logs${this.q({ ...options, backing })}`,
    );
    return r.items;
  }

  async getMetrics(
    name: string,
    rangeMinutes = 60,
    backing?: EnvironmentBacking,
  ) {
    return this.request<EnvironmentMetrics>(
      `/environments/${this.seg(name)}/metrics${this.q({ rangeMinutes, backing })}`,
    );
  }

  async runKubectl(name: string, args: string[], backing?: EnvironmentBacking) {
    return this.request<KubectlResult>(
      `/environments/${this.seg(name)}/kubectl${this.q({ backing })}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ args }),
      },
    );
  }
}
